import { useState } from 'react';
import { useWidget } from '../WidgetProvider';
import { getSelectionsForRoom, getSelectionsForProjectType, getProjectTypes, STYLE_PRESETS } from '../config/roomOptions';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp, Paintbrush, Check } from 'lucide-react';
import { ColorPicker } from '../ColorPicker';
import type { RenovationSelections, SherwinWilliamsColor, ProjectTypeConfig } from '@/types/firestore';
import { DEFAULT_FEATURED_COLORS } from '@/data/sherwin-williams-colors';
import type { SelectionCategory } from '../config/roomOptions';

export function SelectionsStep() {
  const {
    state,
    updateSelections,
    updateProjectTypeSelections,
    setStylePreference,
    setAdditionalDetails,
    nextStep,
    trackEvent,
    tenantConfig,
  } = useWidget();

  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
    new Set(Object.keys(state.selections))
  );
  // Track which project type accordions are open
  const [openProjectTypes, setOpenProjectTypes] = useState<string[]>(
    state.projectTypes.length > 0 ? [state.projectTypes[0]] : []
  );

  // Get project types if any were selected
  const hasProjectTypes = state.projectTypes.length > 0;
  const projectTypeConfigs: ProjectTypeConfig[] = hasProjectTypes && state.roomType
    ? (getProjectTypes(state.roomType, tenantConfig?.widget?.room_config) || [])
        .filter(pt => state.projectTypes.includes(pt.id))
    : [];

  // Get default categories for room (used when no project types selected)
  const defaultCategories = state.roomType ? getSelectionsForRoom(state.roomType) : [];

  // Helper to get selections for a project type or default
  const getSelectionsForDisplay = (projectTypeId?: string): RenovationSelections => {
    if (projectTypeId && state.projectTypeSelections[projectTypeId]) {
      return state.projectTypeSelections[projectTypeId];
    }
    return state.selections;
  };

  const toggleCategory = (categoryId: string, projectTypeId?: string) => {
    const selections = getSelectionsForDisplay(projectTypeId);
    const isSelected = categoryId in selections;

    if (isSelected) {
      // Remove selection
      if (projectTypeId) {
        const newSelections = { ...state.projectTypeSelections[projectTypeId] };
        delete newSelections[categoryId as keyof RenovationSelections];
        updateProjectTypeSelections(projectTypeId, newSelections);
      } else {
        const newSelections = { ...state.selections };
        delete newSelections[categoryId as keyof RenovationSelections];
        updateSelections(newSelections);
      }
    } else {
      // Expand to show options
      setExpandedCategory(`${projectTypeId || 'default'}-${categoryId}`);
    }
  };

  const selectOption = (
    categoryId: string,
    optionId: string,
    categories: SelectionCategory[],
    projectTypeId?: string
  ) => {
    const category = categories.find((c) => c.id === categoryId);
    if (!category) return;

    const selections = getSelectionsForDisplay(projectTypeId);
    const currentSelection = getSelectedOption(categoryId, selections);

    // Allow deselecting
    if (currentSelection === optionId) {
      if (projectTypeId) {
        const newSelections = { ...state.projectTypeSelections[projectTypeId] };
        delete newSelections[categoryId as keyof RenovationSelections];
        updateProjectTypeSelections(projectTypeId, newSelections);
      } else {
        const newSelections = { ...state.selections };
        delete newSelections[categoryId as keyof RenovationSelections];
        updateSelections(newSelections);
      }
      return;
    }

    // Build selection value based on category
    let selectionValue: Record<string, unknown>;

    switch (categoryId) {
      case 'flooring':
        selectionValue = {
          type: optionId,
          tier: ['hardwood', 'tile', 'concrete'].includes(optionId) ? 'premium' : 'standard',
        };
        break;
      case 'walls': {
        const existingColor = (selections.walls as { color?: SherwinWilliamsColor })?.color;
        selectionValue = {
          type: optionId as 'paint' | 'wallpaper' | 'tile',
          color: optionId === 'paint' ? existingColor : undefined,
        };
        break;
      }
      case 'cabinets': {
        const existingColor = (selections.cabinets as { color?: SherwinWilliamsColor })?.color;
        selectionValue = {
          style: optionId as 'refinish' | 'replace',
          color: existingColor,
        };
        break;
      }
      case 'countertops':
        selectionValue = { material: optionId };
        break;
      case 'lighting':
        selectionValue = { type: optionId };
        break;
      case 'fixtures':
        selectionValue = { tier: optionId };
        break;
      default:
        // For custom/outdoor categories, use generic structure
        selectionValue = { type: optionId };
    }

    if (projectTypeId) {
      updateProjectTypeSelections(projectTypeId, { [categoryId]: selectionValue } as Partial<RenovationSelections>);
    } else {
      updateSelections({ [categoryId]: selectionValue } as Partial<RenovationSelections>);
    }
  };

  const getSelectedOption = (categoryId: string, selections: RenovationSelections): string | null => {
    const selection = selections[categoryId as keyof RenovationSelections];
    if (!selection || typeof selection !== 'object') return null;

    if ('type' in selection) return selection.type as string;
    if ('style' in selection) return selection.style as string;
    if ('material' in selection) return selection.material as string;
    if ('tier' in selection) return selection.tier as string;
    return null;
  };

  // Color handling
  const handleColorChange = (
    categoryId: 'walls' | 'cabinets',
    color: SherwinWilliamsColor | undefined,
    projectTypeId?: string
  ) => {
    const selections = getSelectionsForDisplay(projectTypeId);
    const current = selections[categoryId];
    if (!current) return;

    const updated = { ...current, color };

    if (projectTypeId) {
      updateProjectTypeSelections(projectTypeId, { [categoryId]: updated } as Partial<RenovationSelections>);
    } else {
      updateSelections({ [categoryId]: updated } as Partial<RenovationSelections>);
    }
  };

  // Get tenant's featured colors
  const tenantFeaturedColors = tenantConfig?.widget?.featured_paint_colors || DEFAULT_FEATURED_COLORS;
  const allowColorSearch = tenantConfig?.widget?.allow_color_search !== false;
  const tenantCustomColors = tenantConfig?.custom_colors || [];

  // Check if we can continue
  const hasSelections = hasProjectTypes
    ? Object.keys(state.projectTypeSelections).some(
        (ptId) => Object.keys(state.projectTypeSelections[ptId] || {}).length > 0
      )
    : Object.keys(state.selections).length > 0;
  const canContinue = hasSelections && state.stylePreference;

  // Render category card
  const renderCategoryCard = (
    category: SelectionCategory,
    selections: RenovationSelections,
    projectTypeId?: string
  ) => {
    const categoryKey = `${projectTypeId || 'default'}-${category.id}`;
    const isExpanded = expandedCategory === categoryKey;
    const selectedOption = getSelectedOption(category.id, selections);
    const isSelected = category.id in selections;

    return (
      <Card
        key={categoryKey}
        className={cn(
          'overflow-hidden transition-all',
          isSelected && 'border-primary'
        )}
      >
        <div
          className="p-4 flex items-center gap-3 cursor-pointer"
          onClick={() => {
            if (isSelected) {
              setExpandedCategory(isExpanded ? null : categoryKey);
            } else {
              setExpandedCategory(categoryKey);
            }
          }}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => toggleCategory(category.id, projectTypeId)}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium">{category.label}</p>
              {isSelected && selectedOption && !isExpanded && (
                <Badge
                  variant="secondary"
                  className="text-[10px] h-5 px-1.5 bg-primary/10 text-primary border-primary/20 shrink-0"
                >
                  {category.options.find((o) => o.id === selectedOption)?.label}
                </Badge>
              )}
              {isSelected && !selectedOption && !isExpanded && (
                <Badge
                  variant="outline"
                  className="text-[10px] h-5 px-1.5 text-muted-foreground shrink-0"
                >
                  Tap to select
                </Badge>
              )}
            </div>
          </div>
          {isSelected && (
            <Button variant="ghost" size="icon" className="shrink-0">
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          )}
        </div>

        {isExpanded && (
          <div className="px-4 pb-4 pt-0 border-t">
            <div className="grid grid-cols-2 gap-2 mt-3">
              {category.options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => selectOption(category.id, option.id, [category], projectTypeId)}
                  className={cn(
                    'p-3 rounded-lg border text-left transition-all',
                    selectedOption === option.id
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-muted-foreground/50'
                  )}
                >
                  <p className="font-medium text-sm">{option.label}</p>
                  {option.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{option.description}</p>
                  )}
                </button>
              ))}
            </div>

            {/* Color pickers */}
            {category.id === 'walls' && selectedOption === 'paint' && (
              <div className="mt-4 pt-4 border-t border-dashed">
                <ColorPicker
                  label="Wall Paint Color"
                  value={(selections.walls as { color?: SherwinWilliamsColor })?.color}
                  onChange={(color) => handleColorChange('walls', color, projectTypeId)}
                  featuredColorCodes={tenantFeaturedColors}
                  customColors={tenantCustomColors}
                  allowSearch={allowColorSearch}
                  required={false}
                />
              </div>
            )}
            {category.id === 'cabinets' && selectedOption && (
              <div className="mt-4 pt-4 border-t border-dashed">
                <ColorPicker
                  label="Cabinet Color"
                  value={(selections.cabinets as { color?: SherwinWilliamsColor })?.color}
                  onChange={(color) => handleColorChange('cabinets', color, projectTypeId)}
                  featuredColorCodes={tenantFeaturedColors}
                  customColors={tenantCustomColors}
                  allowSearch={allowColorSearch}
                  required={false}
                />
              </div>
            )}
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-lg font-medium">What would you like to change?</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {hasProjectTypes
            ? 'Configure your selections for each project type'
            : 'Select the areas you want to renovate'}
        </p>
      </div>

      {/* Project Type Accordions (when project types selected) */}
      {hasProjectTypes && projectTypeConfigs.length > 0 ? (
        <Accordion
          type="multiple"
          value={openProjectTypes}
          onValueChange={setOpenProjectTypes}
          className="space-y-3"
        >
          {projectTypeConfigs.map((projectType) => {
            const categories = getSelectionsForProjectType(
              projectType.id,
              state.roomType!,
              tenantConfig?.widget?.room_config
            );
            const projectSelections = state.projectTypeSelections[projectType.id] || {};
            const selectionCount = Object.keys(projectSelections).length;

            return (
              <AccordionItem
                key={projectType.id}
                value={projectType.id}
                className="border rounded-lg overflow-hidden"
              >
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30">
                  <div className="flex items-center gap-2 text-left">
                    <span className="font-medium">{projectType.label} Options</span>
                    {selectionCount > 0 && (
                      <Badge variant="secondary" className="text-[10px]">
                        {selectionCount} selected
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-3 pt-2">
                    {categories.length > 0 ? (
                      categories.map((category) =>
                        renderCategoryCard(category, projectSelections, projectType.id)
                      )
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No specific options configured for {projectType.label}
                      </p>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      ) : (
        /* Default Category Selection (no project types) */
        <div className="space-y-3">
          {defaultCategories.map((category) =>
            renderCategoryCard(category, state.selections)
          )}
        </div>
      )}

      {/* Style Preference (shared) */}
      <div className="pt-4">
        <Label className="flex items-center gap-2 mb-3">
          <Paintbrush className="w-4 h-4" />
          Design Style
        </Label>
        <div className="flex flex-wrap gap-2">
          {STYLE_PRESETS.map((style) => {
            const isSelected = state.stylePreference === style.id;
            return (
              <Badge
                key={style.id}
                variant={isSelected ? 'default' : 'outline'}
                className={cn(
                  'cursor-pointer py-1.5 px-3 transition-all text-sm',
                  isSelected
                    ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90 ring-2 ring-primary/20 gap-1'
                    : 'hover:border-primary/50 hover:bg-muted/50'
                )}
                onClick={() => setStylePreference(isSelected ? '' : style.id)}
              >
                {isSelected && <Check className="w-3 h-3" />}
                {style.label}
              </Badge>
            );
          })}
        </div>
        {state.stylePreference && (
          <p className="text-xs text-muted-foreground mt-2">Tap again to deselect</p>
        )}
      </div>

      {/* Additional Details */}
      <div className="pt-4">
        <Label htmlFor="additional-details" className="text-sm font-medium">
          Additional details <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Textarea
          id="additional-details"
          placeholder="Any specific requests or details about your project..."
          className="mt-2 resize-none"
          rows={3}
          maxLength={300}
          value={state.additionalDetails || ''}
          onChange={(e) => setAdditionalDetails(e.target.value || undefined)}
        />
        <p className="text-xs text-muted-foreground mt-1 text-right">
          {(state.additionalDetails?.length || 0)}/300
        </p>
      </div>

      {/* Continue Button */}
      <Button
        className="w-full mt-6 h-12 text-base font-semibold bg-accent hover:bg-accent/90 text-accent-foreground shadow-md shadow-accent/20 transition-all hover:shadow-lg hover:shadow-accent/25 active:scale-[0.98] disabled:opacity-50 disabled:shadow-none"
        size="lg"
        disabled={!canContinue}
        onClick={() => {
          trackEvent('selections_made', {
            project_types: state.projectTypes,
            style: state.stylePreference,
          });
          nextStep();
        }}
      >
        Continue
      </Button>

      {!canContinue && (
        <p className="text-xs text-center text-muted-foreground">
          Select at least one option and a design style
        </p>
      )}
    </div>
  );
}
